import { useCallback } from "react";
import { tz } from "moment-timezone";
import { useAppDispatch } from "store/hooks";
import { selectUser } from "modules/auth/redux/selectors";
import { useSendMessage } from "modules/chat";
import { selectIsChatEnabled } from "modules/event/selectors";
import { useSelector } from "react-redux";
import { UserProfileCard } from "modules/userProfileCard";
import { openUserProfileWindow } from "modules/userProfileWindow/redux/userProfileWindowSlice";
import { makeSelectUserById } from "modules/event/usersInEvent/selectors";
import {
  selectUserProfileCardAnchorEl,
  selectUserProfileCardIsShowProfileCard,
  selectUserProfileCardUserId,
} from "../redux/selectors";
import { closeUserProfileCard } from "../redux/slice";

const UserProfileCardContainer = (): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const user = useSelector(selectUser);
  const { startDirectMessage } = useSendMessage();
  const profileCardDataUserId = useSelector(selectUserProfileCardUserId);
  const profileCardDataUser = useSelector(
    makeSelectUserById(profileCardDataUserId ?? ""),
  );
  const profileCardDataAnchorEl = useSelector(selectUserProfileCardAnchorEl);
  const isShowProfileCard = useSelector(selectUserProfileCardIsShowProfileCard);
  const isMe = user && user.id === profileCardDataUserId;
  const usersTimezone = profileCardDataUser?.profile?.timezone
    ? tz(profileCardDataUser.profile.timezone).format("hh:mma zz")
    : undefined;

  const isChatEnabled = useSelector(selectIsChatEnabled);
  const hasProfileData =
    profileCardDataUser &&
    profileCardDataUser.profile &&
    (profileCardDataUser.profile.headline ||
      profileCardDataUser.profile.company);
  const showRequestInfo = !isMe && !hasProfileData;

  const handleEdit = useCallback(() => {
    dispatch(closeUserProfileCard());
    closeUserProfileCard();
    dispatch(openUserProfileWindow());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMe]);

  const handleStartChat = useCallback(
    (toUserId: string) => {
      if (user) {
        dispatch(closeUserProfileCard());
        startDirectMessage(toUserId, user.id);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, dispatch],
  );

  if (
    !(
      isShowProfileCard &&
      profileCardDataUser &&
      profileCardDataAnchorEl?.current
    )
  ) {
    return null;
  }

  const handleClose = () => {
    dispatch(closeUserProfileCard());
  };

  return (
    <UserProfileCard
      open
      hideMessageButton={!isChatEnabled || isMe}
      showRequestInfo={showRequestInfo}
      data={{
        userId: profileCardDataUserId,
        user: profileCardDataUser,
        anchorEl: profileCardDataAnchorEl,
      }}
      onClose={handleClose}
      onEdit={isMe ? handleEdit : null}
      onStartChat={handleStartChat}
      usersTimezone={usersTimezone}
    />
  );
};

export default UserProfileCardContainer;
